import { Action, ActionSubmit, ActionType } from '@gsc/types'
import { pattern } from '@gsc/utils'
import { AppThunk } from './store'
import {
  goToPrevPage,
  selectTemplateTagsForWidget,
  setNotification,
  setPendingActions,
  closeWidgetView,
} from './widgets'
import { delay, waitForAnimationEnd } from '../helpers/animation'

function getActionPriority(action: Action): number {
  switch (action.type) {
    case ActionType.Submit:
      return 3

    case ActionType.ShowWidget:
    case ActionType.CopyToClipboard:
      return 2

    case ActionType.OpenUrl:
      return 1

    case ActionType.ApplyCoupon:
    case ActionType.AddToCart:
      return action.redirect ? 1 : 2

    case ActionType.Back:
    case ActionType.CloseWidget:
      return 0
  }
}

function sortActions(actions: Action[]): Action[] {
  return [...actions].sort((a, b) => getActionPriority(b) - getActionPriority(a))
}

function getLastSuccessMessage(actions: Action[]): string {
  return actions.reduce((prevMessage, action) => {
    if (
      action.type === ActionType.CopyToClipboard ||
      action.type === ActionType.AddToCart ||
      action.type === ActionType.ApplyCoupon
    ) {
      return action.successMessage || prevMessage
    }
    return prevMessage
  }, '')
}

export const showNotification =
  (widgetId: number, name: string, message: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (!name || !message) {
      return
    }
    const templateTags = selectTemplateTagsForWidget(getState(), widgetId)
    const notification = {
      message: pattern(message, templateTags),
      name,
    }

    dispatch(setNotification(widgetId, notification))
    await delay(1500)

    // lock - if notification was updated, do not clean it up
    if (getState().widgets[widgetId].notification !== notification) {
      return
    }

    dispatch(setNotification(widgetId, null))
  }

export const processAction =
  (widgetId: number, action: Action): AppThunk<Promise<boolean>> =>
  async (dispatch, getState, runtime) => {
    const templateTags = selectTemplateTagsForWidget(getState(), widgetId)

    switch (action.type) {
      case ActionType.Back:
        dispatch(goToPrevPage(widgetId))
        return true

      case ActionType.OpenUrl:
        const url = pattern(action.url, templateTags, encodeURIComponent)
        return runtime.openUrl(widgetId, { url, target: action.target, label: action.label })

      case ActionType.ShowWidget:
        return runtime.showWidget(action.id, widgetId)

      case ActionType.CopyToClipboard:
        return runtime.copyToClipboard(widgetId, pattern(action.text, templateTags))

      case ActionType.AddToCart:
        return runtime.addToCart(widgetId, action.selectionType, action.items, action.redirect)

      case ActionType.ApplyCoupon:
        return runtime.applyCoupon(widgetId, pattern(action.coupon, templateTags), action.redirect)

      case ActionType.CloseWidget:
        return dispatch(closeWidgetView(widgetId, 'main'))
    }

    return true
  }

// splits actions into two parts
// - preActions - performs without closing widget / redirect / navigating back
// - postActions - close / redirect/ back
function prepareActions(actions: Action[]) {
  actions = sortActions(actions)
  const splitIdx = actions.findIndex(a => getActionPriority(a) < 2)
  return splitIdx === -1
    ? {
        preActions: actions,
        postActions: [],
      }
    : {
        preActions: actions.slice(0, splitIdx),
        postActions: actions.slice(splitIdx),
      }
}

function isSubmit(action: Action): action is ActionSubmit {
  return action.type === ActionType.Submit
}

// we should process these actions synchronously to prevent browsers (ie Safari) from blocking window.open
function shouldProcessActionImmediately(a: Action): boolean {
  return a.type === ActionType.CopyToClipboard || (a.type === ActionType.OpenUrl && a.target === '_blank')
}

export const processActions =
  (widgetId: number, actions: Action[], origin?: string, silent = false): AppThunk<Promise<boolean>> =>
  async dispatch => {
    const submit = actions.find(isSubmit)
    if (submit) {
      dispatch(
        setPendingActions(widgetId, {
          actions: actions.filter(a => !isSubmit(a)),
          initiator: submit,
          origin,
        }),
      )
      return true
    }

    const { preActions, postActions } = prepareActions(actions)
    const successMessage = !silent ? getLastSuccessMessage(preActions) : null
    let result = true

    for (const action of preActions) {
      result = result && (await dispatch(processAction(widgetId, action)))
    }

    if (result && successMessage) {
      await dispatch(showNotification(widgetId, origin, successMessage))
    }

    for (const action of postActions) {
      await dispatch(processAction(widgetId, action))
    }

    await waitForAnimationEnd(widgetId)

    return result
  }

export const processPendingActionsBeforeSend =
  (widgetId: number): AppThunk<Promise<boolean>> =>
  async (dispatch, getState) => {
    const { pendingActions: { actions = [], initiator, origin } = {} } = getState().widgets[widgetId]
    dispatch(
      setPendingActions(widgetId, {
        actions: actions.filter(a => !shouldProcessActionImmediately(a)),
        initiator,
        origin,
      }),
    )
    return dispatch(processActions(widgetId, actions.filter(shouldProcessActionImmediately), origin, true))
  }

export const processPendingActions =
  (widgetId: number): AppThunk<Promise<boolean>> =>
  async (dispatch, getState) => {
    const { pendingActions: { actions = [], origin } = {} } = getState().widgets[widgetId]
    dispatch(setPendingActions(widgetId, { actions: [] }))
    return dispatch(processActions(widgetId, actions, origin, true))
  }
